import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../../../App.styles';
import { useThemeContext } from '../../../../../global-state/themeContext';
import { BopisData } from '../../../../../hooks';
import { Icons, Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { homeTranslations } from '../../../../../translations';
import { OrderBreakdownItem } from './OrderBreakdownItem';

export type BopisOrdersContentProps = {
  bopisData: BopisData;
  totalOrders: number;
  totalProcessedOrders: number;
};

export const BopisOrdersContent: FC<BopisOrdersContentProps> = ({
  bopisData,
  totalOrders,
  totalProcessedOrders,
}) => {
  const { mode } = useThemeContext();
  const isDarkMode = mode === 'dark';

  const { t } = useTranslation();
  const { bopis: bopisTranslations } = homeTranslations;

  return (
    <ColumnFlexContainer gap={[4]} padding={[2, 5]}>
      <RowFlexContainer alignItems="center" borderRadius={[2]}>
        <ColumnFlexContainer>
          <Typography margin={[0, 0, 1, 0]} type="body3">
            {t(bopisTranslations.processesOrdersTitle.key, bopisTranslations.processesOrdersTitle.defaultValue)}
          </Typography>
          <SecondaryTextTypography type="caption">
            {t(bopisTranslations.shareOfOrders.key, {
              defaultValue: bopisTranslations.shareOfOrders.defaultValue,
              totalCount: totalOrders.toLocaleString(),
            })}
          </SecondaryTextTypography>
        </ColumnFlexContainer>

        <ColumnFlexContainer flex={1} alignItems="end">
          <Typography type="h4" margin={[0, 0, 2, 0]}>
            {totalProcessedOrders.toLocaleString()}
          </Typography>
        </ColumnFlexContainer>
      </RowFlexContainer>

      <ColumnFlexContainer gap={[2, 0]} flex={1} justifyContent="end">
        <OrderBreakdownItem
          icon={Icons.CHECK_MARK}
          text={t(bopisTranslations.statusFullyFulfilled.key, bopisTranslations.statusFullyFulfilled.defaultValue)}
          iconColor={isDarkMode ? 'success' : 'validationGreen'}
          count={bopisData.processed.fullyFulfilled}
          totalCount={totalProcessedOrders}
          tooltipText={t(
            bopisTranslations.fullyFulfilledTooltipText.key,
            bopisTranslations.fullyFulfilledTooltipText.defaultValue,
          )}
        />

        <OrderBreakdownItem
          icon={Icons.BOXCLOSE}
          text={t(
            bopisTranslations.statusPartiallyFulfilled.key,
            bopisTranslations.statusPartiallyFulfilled.defaultValue,
          )}
          iconColor={isDarkMode ? 'warningLight' : 'warningDark'}
          count={bopisData.processed.partiallyFulfilled}
          totalCount={totalProcessedOrders}
          tooltipText={t(
            bopisTranslations.partiallyFulfilledTooltipText.key,
            bopisTranslations.partiallyFulfilledTooltipText.defaultValue,
          )}
        />

        <OrderBreakdownItem
          icon={Icons.CLOSE}
          text={t(bopisTranslations.statusCancelled.key, bopisTranslations.statusCancelled.defaultValue)}
          iconColor={isDarkMode ? 'error' : 'red'}
          count={bopisData.processed.cancelled}
          totalCount={totalProcessedOrders}
          tooltipText={t(
            bopisTranslations.cancelledTooltipText.key,
            bopisTranslations.cancelledTooltipText.defaultValue,
          )}
        />

        <OrderBreakdownItem
          icon={Icons.WARNING}
          text={t(bopisTranslations.statusZeroPicked.key, bopisTranslations.statusZeroPicked.defaultValue)}
          iconColor={isDarkMode ? 'borderGray' : 'secondaryDarkTextGray'}
          count={bopisData.processed.zeroPicked}
          totalCount={totalProcessedOrders}
          tooltipText={t(
            bopisTranslations.zeroPickedTooltipText.key,
            bopisTranslations.zeroPickedTooltipText.defaultValue,
          )}
        />
      </ColumnFlexContainer>
    </ColumnFlexContainer>
  );
};
